class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.type = this.dataset.type || 'product';
    this.isOpen = false;

    if(this.predictiveSearchResults) {
      this.predictiveSearchResultsInitialHTML = this.predictiveSearchResults.innerHTML;
    }

    this.setupEventListeners();
  }

  setupEventListeners() {
    const form = this.querySelector('form.search');
    if(form) {
      form.addEventListener('submit', this.onFormSubmit.bind(this));
    }

    if(this.input) {
      this.input.addEventListener('input', debounce((event) => {
        this.onChange(event);
      }, 300).bind(this));
      this.input.addEventListener('focus', this.onFocus.bind(this));
    }
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
    if(this.dataset.partSearch) {
      this.input.setCustomValidity("Please select a part.");
      this.bindPartEventListeners();
    }
  }

  bindPartEventListeners() {
    this.querySelectorAll('.predictive-search__item:not(.selected-part)').forEach((search_item) =>
      search_item.addEventListener('click', this.onPartAdd.bind(this))
    );
    this.querySelector('.remove-part').addEventListener('click', this.onPartRemove.bind(this));
    this.querySelector('[type="reset"]').addEventListener('click', this.onResetPartField.bind(this));
  }

  onResetPartField(event) {
    event.preventDefault();
    this.input.value = '';
  }

  onPartAdd(event) {
    const searchItemHTML = event.currentTarget.innerHTML;
    this.querySelector('.selected-part').innerHTML = searchItemHTML;
    this.classList.add('part-added');
    this.querySelector('#ContactForm-part').value = this.querySelector('.selected-part [data-product-sku]').dataset.productSku;
    this.querySelector('#ContactForm-part').name = 'contact[Part Number]';
    this.querySelector('#confirm-part').setAttribute('required',true);
    this.querySelector('#confirm-part').setAttribute('type','checkbox');
    this.querySelector('[for="confirm-part"]').classList.add('field__checkbox');
    this.querySelector('[for="confirm-part"]').classList.remove('field__hidden');
    this.input.setAttribute('type','hidden');

    this.customConfirmationValidation();

    this.close(true);
  }

  onPartRemove(event) {
    this.classList.remove('part-added');
    this.querySelector('.selected-part').innerHTML = '';
    this.bindPartEventListeners();
    this.querySelector('#confirm-part').checked = false;
    this.querySelector('#confirm-part').removeAttribute('required');
    this.querySelector('#confirm-part').setAttribute('type','hidden');
    this.querySelector('[for="confirm-part"]').classList.add('field__hidden');
    this.querySelector('[for="confirm-part"]').classList.remove('field__checkbox');
    this.querySelector('#ContactForm-part').value = '';
    this.querySelector('#ContactForm-part').name = '';
    this.input.setAttribute('type','search');

    this.customConfirmationValidation();
  }

  customConfirmationValidation() {
    /* Add custom validation message for part confirmation checkbox */
    this.querySelector('#confirm-part').setCustomValidity("You need to click this box.");
    this.querySelector('#confirm-part').addEventListener('change', e => {
      if (!this.querySelector('#confirm-part').checked) {
        this.querySelector('#confirm-part').setCustomValidity("You need to click this box.");
      } else {
        this.querySelector('#confirm-part').setCustomValidity("");
      }
    }, true);
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.close(true);
      if(this.dataset.partSearch) {
        this.bindPartEventListeners();
      }
      return;
    }

    this.getSearchResults(searchTerm);

    if(this.dataset.partSearch) {
      /* Add custom validation message for part search field */
      if (this.input.value == "" || this.input.value != "" && !this.classList.contains('part-added')) {
        this.input.setCustomValidity("Please select a part.");
      } else {
        this.input.setCustomValidity("");
      }
    }
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFocus() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.open();
    } else {
      if (this.getAttribute('results') === 'true') {
        this.open();
      } else {
        this.getSearchResults(searchTerm);
      }
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) { 
        this.removeAttribute('open');
        this.close(); 
      }
    })
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (
      event.code === 'ArrowUp' ||
      event.code === 'ArrowDown'
    ) {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allElements = this.querySelectorAll('li');
    let activeElement = this.querySelector('li');

    if (moveUp && !selectedElement) return;

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElement = selectedElement.nextElementSibling || allElements[0];
    } else if (moveUp) {
      activeElement = selectedElement.previousElementSibling || allElements[allElements.length - 1];
    }

    if (activeElement === selectedElement) return;

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);
    console.log(selectedElement);

    this.setLiveRegionText(activeElement.textContent);
    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedProduct = this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button, [aria-selected="true"] div');
    console.log(selectedProduct);

    if (selectedProduct) selectedProduct.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    let fetchURL = `${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&${encodeURIComponent('resources[type]')}=${this.type}&${encodeURIComponent('resources[limit]')}=1&section_id=predictive-search`;
    if(this.dataset.partSearch) {
      fetchURL = `${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&${encodeURIComponent('resources[type]')}=product&resources[options][fields]=variants.sku&section_id=part-search`
    }
    fetch(fetchURL)
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const sectionId = (this.dataset.partSearch) ? '#shopify-section-part-search' : '#shopify-section-predictive-search';
        const resultsMarkup = new DOMParser().parseFromString(text, 'text/html').querySelector(sectionId).innerHTML;
        this.cachedResults[queryKey] = resultsMarkup;
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
    if(this.dataset.partSearch) {
      this.bindPartEventListeners();
    }
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight = window.visualViewport.height - document.getElementById('shopify-section-header').getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');

      this.predictiveSearchResults.innerHTML = this.predictiveSearchResultsInitialHTML;
    }

    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false
    this.predictiveSearchResults.removeAttribute('style');

    this.isOpen = false;
  }
}

customElements.define('predictive-search', PredictiveSearch);